import type { RefObject } from 'react';
import { useEffect } from 'react';
import { useVariableWithStableRef } from './useVariableWithStableRef';

export function useClickOutsideElementObserver<TElement extends Node>(element: TElement | null, callback: () => void) {
    const handlerRef = useVariableWithStableRef(callback);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) {
                return;
            }

            function isChildOfReferencedElement(elem: Node): boolean {
                return element !== null && element.contains(elem);
            }

            // We check if the target element is still present in document because it might happen
            // that click event causes component to unmount and simply checking if element contains the target
            // would give false negatives.
            // element's event handler => unmounting => this event handler
            function isAttachedToDocument(elem: Node): boolean {
                return document.body.contains(elem);
            }

            const isClickOutside = !isChildOfReferencedElement(event.target) && isAttachedToDocument(event.target);
            isClickOutside && handlerRef.current?.();
        };

        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, [element, handlerRef]);
}

export function useClickOutsideRefObserver<TElement extends Node>(
    ref: RefObject<TElement | null>,
    callback: () => void
) {
    useClickOutsideElementObserver(ref.current, callback);
}
